import { FC, memo, useEffect, useMemo } from 'react'

import { useGetContractPlanQuery } from '@crew/apis/contract/contractApis'
import { SettingKeyType } from '@crew/enums/app'
import { useValueChangeEffect } from '@crew/hooks'
import {
  useSubscribeAnnouncement,
  useSubscribeUnread,
  useSubscribeUserSetting,
  useWebsocketPauser,
} from '@crew/providers/websocket'
import {
  useLoadPresenceStates,
  useLoadUserSettings,
  useUserSetting,
} from '@crew/states'

import {
  closeAiAssistantWindow,
  setCurrentPlan,
} from 'features/app/states/appSlice'
import { useChangeDocumentTitle } from 'hooks/useChangeDocumentTitle'
import { useAppDispatch, useAppSelector } from 'states/hooks'

import { AppBar } from '../appBar/appBar'
import { AppContentPanel } from '../appContentPanel/appContentPanel'
import { AppLeftSideBar } from '../appLeftSideBar/appLeftSideBar'
import { AppRequestNotificationPermissionBar } from '../appRequestNotificationPermissionBar/appRequestNotificationPermissionBar'
import { AppRightSideBar } from '../appRightSideBar/appRightSideBar'
import { AppAiAssistantWindow } from '../appAiAssistantWindow/appAiAssistantWindow'
import classNames from 'classnames'
import { Fullstar } from 'modules/fullstar'

export const AppContainer: FC = memo(() => {
  useChangeDocumentTitle()

  const dispatch = useAppDispatch()
  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)
  const [loadUserSettings] = useLoadUserSettings()
  const [loadPresenceStates] = useLoadPresenceStates()
  const { resumeConnection } = useWebsocketPauser()

  // 個人設定関連のsubscribeを行う
  useSubscribeUserSetting(dispatch, useAppSelector)

  // 個人設定をAPI経由で取得してReduxに保存する
  useValueChangeEffect(
    () => {
      if (loggedInUser) {
        loadUserSettings()

        // Get presence states
        loadPresenceStates()

        // WebSocketの接続を行う
        resumeConnection()
      }
    },
    [loggedInUser, loadUserSettings, loadPresenceStates, resumeConnection],
    loggedInUser
  )

  // 未読件数のsubscribeを行う
  useSubscribeUnread()

  // お知らせのsubscribeを行う
  useSubscribeAnnouncement()

  // get contract data information
  const { data: getContractPlanResult } = useGetContractPlanQuery()

  //set contractPlan to redux
  useEffect(() => {
    if (getContractPlanResult) {
      dispatch(setCurrentPlan(getContractPlanResult.plan))
    }
  }, [dispatch, getContractPlanResult])

  // aiアシスタントウィンドウの表示状態
  const aiAssistantWindowOpened = useAppSelector(
    (state) => state.app.aiAssistantWindowOpened
  )

  // AIアシスタントウィンドウが最大サイズかどうか
  const isAiAssistantWindowLargeHeight = useAppSelector(
    (state) => state.app.isAiAssistantWindowLargeHeight
  )

  // aiAssistantWindowのクエリ対象になる情報を取得する
  const aiAssistantChannnelTargetEntity = useAppSelector(
    (state) => state.message.chat.aiAssistantChannel.displayTargetEntity
  )

  // aiアシスタントウィンドウを表示してよいかチェックする
  // 各詳細画面が表示されたタイミングで、aiAssistantChannel.displayTargetEntityの値を格納するため、その値の有無で、ボタンの表示非表示を判定する
  // 各詳細画面側で、アンマウント時は逆にtargetEntityが空になるので、非表示になる
  const isVisibleAiAssistantWindow = useMemo(() => {
    // aiアシスタントのクエリの対象が存在する場合は表示する
    if (
      aiAssistantChannnelTargetEntity?.entityType &&
      aiAssistantChannnelTargetEntity?.entityRecordId
    ) {
      return true
    }

    // aiアシスタントのクエリの対象が存在しない場合は、表示しない
    // かつウィンドウの表示状態もfalseにしておく（一覧 -> 詳細画面に遷移したときにAIアシスタントウィンドウが開かないようにするため
    dispatch(closeAiAssistantWindow())

    return false
  }, [
    aiAssistantChannnelTargetEntity?.entityRecordId,
    aiAssistantChannnelTargetEntity?.entityType,
    dispatch,
  ])

  // チュートリアル(操作ガイド)の表示について個人設定を取得する
  // 有効にしていないのに表示されてしまうのを防ぐため、デフォルトをfalseにして個人設定値が取得できるまでは処理が走らないようにしておく
  const displayTutorialPersonalSetting = useUserSetting(
    SettingKeyType.DisplayTutorial,
    'false'
  )

  // チュートリアル(操作ガイド)の表示設定切り替え時、
  // fullstarのチェックリスト(？ボタン)を非表示にするためのクラスをbodyに付与/削除する
  useValueChangeEffect(
    () => {
      if (displayTutorialPersonalSetting === 'false') {
        document.body.classList.add('fullstar-checklist-disable')
      } else {
        document.body.classList.remove('fullstar-checklist-disable')
      }
    },
    [displayTutorialPersonalSetting],
    displayTutorialPersonalSetting
  )

  return (
    <div
      id="app-container"
      data-testid="app-container"
      // CREW-14584で報告されているように、ウィンドウ幅 < 750pxの場合、トップメニューのレイアウトが崩れる
      // 上記のエラーを発生しないように、コンテナの最小幅を750pxに設定する
      className="flex flex-col h-[100dvh] min-w-[750px]"
    >
      <AppBar />
      <AppRequestNotificationPermissionBar />
      <div className="flex flex-row flex-1 relative overflow-hidden">
        <AppLeftSideBar />
        <div
          id="app-content-panel-and-right-side-bar"
          className="flex flex-row min-w-0 flex-1"
        >
          <AppContentPanel />
          <AppRightSideBar />
        </div>
        {/** aiAssistantWindowはフロート表示 */}
        {isVisibleAiAssistantWindow && aiAssistantWindowOpened ? (
          <div
            className={classNames(
              'fixed z-20 right-4 bottom-4',
              isAiAssistantWindowLargeHeight ? 'top-14' : 'h-2/3'
            )}
          >
            <AppAiAssistantWindow />
          </div>
        ) : null}
      </div>
      {/**
       * Fullstarの初期化をログイン後に行うため、ここに設置する
       */}
      {<Fullstar />}
    </div>
  )
})
